import { useCallback, useEffect, useRef, useState } from 'react';
import type { AtMentionItem } from './AtMentionContext';
import { useAtMentionContext } from './AtMentionContext';

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 10;

type UseAtMentionSuggestionsResult = {
  items: AtMentionItem[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
};

export function useAtMentionSuggestions(query: string, enabled: boolean): UseAtMentionSuggestionsResult {
  const [, fetchSubjectSuggestions] = useAtMentionContext();
  const [items, setItems] = useState<AtMentionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep a ref so the async callback always sees the latest fetcher without
  // making fetchSubjectSuggestions a useEffect dependency (which can change
  // reference on every context re-creation and cause unwanted extra fetches).
  const fetchRef = useRef(fetchSubjectSuggestions);
  fetchRef.current = fetchSubjectSuggestions;

  // Reset pagination when the query or enabled state changes
  useEffect(() => {
    setOffset(0);
    setItems([]);
    setHasMore(false);
  }, [query, enabled]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!enabled) {
      setItems([]);
      setIsLoading(false);
      setHasMore(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    debounceRef.current = setTimeout(() => {
      if (cancelled) return;

      void fetchRef
        .current(query, { limit: PAGE_SIZE, offset })
        .then((res) => {
          if (cancelled) return;
          const newItems = res.results.slice(0, PAGE_SIZE).map((r) => ({
            entityId: String(r.entityId),
            objectId: r.objectId != null ? String(r.objectId) : undefined,
            name: r.name,
            type: r.type,
            link: r.link,
          }));
          setItems((prev) => (offset === 0 ? newItems : [...prev, ...newItems]));
          setHasMore(res.results.length >= PAGE_SIZE);
        })
        .catch(() => {
          if (!cancelled) {
            setItems([]);
            setHasMore(false);
          }
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // offset is intentionally included: re-fetch when page increments
  }, [query, enabled, offset]); // fetchRef is excluded — it's a ref that's always up-to-date

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    setOffset((prev) => prev + PAGE_SIZE);
  }, [hasMore, isLoading]);

  return { items, isLoading, hasMore, loadMore };
}
